import type { ServerDisks as ServerDisksData, ServiceMetric } from '@bekebi/contracts/api';
import { METRICS } from '@bekebi/domain';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { formatBytes } from '../mailboxes/quota';
import { useServerDisks, useServiceMetrics } from './api';
import { DefinitionList, Section, UnavailableNote } from './components';
import { exactBytes, formatDateTime, formatGigabytes, formatRelative } from './format';

/**
 * Cloud Server statistics (docs/integrations/nextcloud.md §2), in two clearly separate groups:
 *  - what Nextcloud reports about itself (users, files, activity, database size);
 *  - host figures as Nextcloud reads them from its server (load, memory, free space on its data
 *    volume) — NOT independent host monitoring, which is not connected.
 * Only values the API returns are shown. A metric never collected is "Not collected"; an old one
 * is marked out of date rather than shown as current.
 */
export function CloudServerMetrics({ serviceId }: { serviceId: string }) {
  const query = useServiceMetrics(serviceId);
  // Server internals (load, memory, swap) are Admin-only; the API does not send them to other
  // roles, so their rows are left out rather than shown as "Not collected".
  const technical = useHasPermission('monitoring.view');
  const hostRows = HOST_ROWS.filter(
    ([, keys]) => technical || keys.every((k) => METRICS[k].layer === 'service'),
  );
  if (query.isPending) return <LoadingState label="Loading Cloud Server statistics…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Cloud Server statistics could not be loaded"
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const { metrics, collectionIntervalMinutes } = query.data;
  const byKey = new Map(metrics.map((m) => [m.key, m]));
  const collected = metrics.length > 0;
  const every = collectionIntervalMinutes ? ` every ${collectionIntervalMinutes} minutes` : '';

  return (
    <>
      <Section
        title="Cloud application"
        description="What the Cloud Server's application reports about its own use."
      >
        {collected ? (
          <DefinitionList
            items={APPLICATION_ROWS.map(([label, keys]) => [
              label,
              <MetricValue keys={keys} of={byKey} />,
            ])}
          />
        ) : (
          <NotCollected every={every} />
        )}
      </Section>

      <Section
        title="Host, as reported by the application"
        description="Read by the Cloud Server's application from the server it runs on. This is not independent host monitoring: these figures stop updating if the application is down, and free space covers the application's data volume only."
      >
        {collected ? (
          <div className="space-y-4">
            <DefinitionList
              items={hostRows.map(([label, keys]) => [
                label,
                <MetricValue keys={keys} of={byKey} bytes={formatGigabytes} />,
              ])}
            />
            <UnavailableNote>
              CPU and memory monitoring independent of the application (while it is down, for
              example) is not connected.
            </UnavailableNote>
          </div>
        ) : (
          <UnavailableNote>
            Host status and server metrics (CPU, memory, disk) are not connected. The application
            status check shows whether the application answers correctly; it does not prove the
            server itself is healthy.
          </UnavailableNote>
        )}
      </Section>

      {technical ? <ServerDisks serviceId={serviceId} /> : null}
    </>
  );
}

/**
 * The Cloud Server's disks, read directly from the server over SSH (U-8 (a)). Admin only. Every
 * real filesystem of the latest reading; an old reading is marked out of date, never shown as now.
 */
function ServerDisks({ serviceId }: { serviceId: string }) {
  const query = useServerDisks(serviceId, true);
  return (
    <Section
      title="Server disks"
      description="Read directly from the Cloud Server over SSH (df). These are the server's own disks — not Cloud user quotas and not the Storage Bucket."
    >
      {query.isPending ? (
        <LoadingState label="Loading server disks…" />
      ) : query.isError ? (
        <ErrorState
          title="Server disks could not be loaded"
          message={query.error.message}
          onRetry={() => void query.refetch()}
        />
      ) : query.data.observedAt === null ? (
        <UnavailableNote>
          The server&apos;s disks have not been read yet: either the restricted SSH account is not
          set up, or reading is failing (the worker log records the reason as a fixed code). Once it
          works, every filesystem is read
          {query.data.collectionIntervalMinutes
            ? ` every ${query.data.collectionIntervalMinutes} minutes`
            : ''}
          . No disk figures are shown until a reading arrives.
        </UnavailableNote>
      ) : (
        <DiskTable data={query.data} observedAt={query.data.observedAt} />
      )}
    </Section>
  );
}

function DiskTable({ data, observedAt }: { data: ServerDisksData; observedAt: string }) {
  return (
    <div className="space-y-3">
      <p className={`text-sm ${data.stale ? 'font-medium text-warning' : 'text-muted'}`}>
        <span title={formatDateTime(observedAt)}>
          {data.stale ? 'Out of date — last read ' : 'Read '}
          {formatRelative(observedAt)} ({formatDateTime(observedAt)})
        </span>
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] text-left text-sm" aria-label="Server disks">
          <thead className="text-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-medium">
                Mount point
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Device
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Type
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Size
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Used
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Free
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                Use
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {data.filesystems.map((fs) => (
              <tr key={`${fs.device} ${fs.mountPoint}`}>
                <td className="break-all py-2 pr-4 font-mono text-xs">{fs.mountPoint}</td>
                <td className="break-all py-2 pr-4 font-mono text-xs text-muted">{fs.device}</td>
                <td className="py-2 pr-4 text-muted">{fs.fsType}</td>
                <td className="py-2 pr-4 text-right" title={exactBytes(fs.totalBytes)}>
                  {formatGigabytes(fs.totalBytes)}
                </td>
                <td className="py-2 pr-4 text-right" title={exactBytes(fs.usedBytes)}>
                  {formatGigabytes(fs.usedBytes)}
                </td>
                <td className="py-2 pr-4 text-right" title={exactBytes(fs.availableBytes)}>
                  {formatGigabytes(fs.availableBytes)}
                </td>
                <td className="py-2 text-right">{fs.usedPercent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type Row = [label: string, keys: (keyof typeof METRICS)[]];

const APPLICATION_ROWS: Row[] = [
  ['User accounts', ['cloud.app.users']],
  ['Files', ['cloud.app.files']],
  ['Active users (last 5 minutes)', ['cloud.app.active_users_5m']],
  ['Active users (last hour)', ['cloud.app.active_users_1h']],
  ['Active users (last 24 hours)', ['cloud.app.active_users_24h']],
  ['Database size', ['cloud.app.database_size_bytes']],
];

const HOST_ROWS: Row[] = [
  [
    'System load (1, 5, 15 minutes)',
    ['cloud.host.load_1m', 'cloud.host.load_5m', 'cloud.host.load_15m'],
  ],
  ['Memory available', ['cloud.host.memory_available_bytes', 'cloud.host.memory_total_bytes']],
  ['Swap free', ['cloud.host.swap_free_bytes', 'cloud.host.swap_total_bytes']],
  ['Free space on the data volume', ['cloud.host.data_free_bytes']],
];

function NotCollected({ every }: { every: string }) {
  return (
    <UnavailableNote>
      Cloud Server statistics have not been collected yet: either the statistics access is not set
      up, or collection is failing (the worker log records the reason as a fixed code). Once it
      works, they are collected{every}. No figures are shown until a reading arrives.
    </UnavailableNote>
  );
}

function formatValue(metric: ServiceMetric, bytes: (n: number) => string = formatBytes): string {
  switch (metric.unit) {
    case 'bytes':
      return bytes(metric.value);
    case 'load':
      return metric.value.toFixed(2);
    default:
      return new Intl.NumberFormat('en-GB').format(metric.value);
  }
}

/**
 * One metric row (shared by the Cloud Server and Storage Bucket pages): a single value, "a of b"
 * for available/total pairs, or a list of load averages.
 */
export function MetricValue({
  keys,
  of,
  bytes,
}: {
  keys: ServiceMetric['key'][];
  of: Map<string, ServiceMetric>;
  /** How byte values are written; decimal (formatBytes) unless given. */
  bytes?: (n: number) => string;
}) {
  const found = keys.map((k) => of.get(k));
  const present = found.filter((m): m is ServiceMetric => m !== undefined);
  if (present.length === 0) return <span className="text-muted">Not collected</span>;

  let text: string;
  if (keys.length === 2 && keys[1]!.endsWith('_total_bytes')) {
    const [part, total] = found;
    if (total && total.value === 0) text = 'None configured';
    else if (part && total) text = `${formatValue(part, bytes)} of ${formatValue(total, bytes)}`;
    else text = part ? formatValue(part, bytes) : `Total ${formatValue(total!, bytes)}`;
  } else {
    text = found.map((m) => (m ? formatValue(m, bytes) : '—')).join(' · ');
  }

  // The oldest of the values shown decides the time and whether the row is out of date.
  const oldest = present.reduce((a, b) => (a.observedAt <= b.observedAt ? a : b));
  const stale = present.some((m) => m.stale);
  const manual = present.some((m) => m.source === 'manual');
  return (
    <span>
      {text}
      <span className={`ml-2 text-xs ${stale ? 'font-medium text-warning' : 'text-muted'}`}>
        <span title={formatDateTime(oldest.observedAt)}>
          {stale ? 'Out of date — ' : ''}
          {manual ? 'entered manually' : 'collected'} {formatRelative(oldest.observedAt)}
        </span>
      </span>
    </span>
  );
}
